import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, ShieldCheck, Package, Layers } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Textarea } from '../../components/ui/Textarea.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { useCart } from '../../store/cart.jsx';
import { hasUnknownPriceItems } from '../../store/cart-state.js';
import { submitInquiry, submitOrder } from '../../lib/api.js';
import { formatCurrency } from '../../lib/utils.js';

/** K3: Telefon – mindestens 6 Ziffern, nur Ziffern übliche Trennzeichen (+, Leerzeichen, -, /, Klammern). */
export function isValidPhone(value) {
  const v = String(value || '').trim();
  return /^\+?[0-9][0-9 \-\/()]{4,}[0-9]$/.test(v) && v.replace(/\D/g, '').length >= 6;
}
import { ROLE_LABELS, ROLE_COLORS } from '../../components/copilot/BomCard.jsx';

export function QuoteRequestForm({ onSuccess, onNavigate, stripeCheckout = false }) {
  const { tenant } = useTenant();
  const { items, customer, updateCustomer, clearCart, totalCents, subtotalCents } = useCart();
  const priceUnknown = hasUnknownPriceItems(items);

  const [formData, setFormData] = useState({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    company: customer?.company || '',
    street: customer?.street || '',
    zip: customer?.zip || '',
    city: customer?.city || '',
    notes: customer?.notes || '',
  });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, val) => {
    setFormData(prev => {
      const next = { ...prev, [field]: val };
      updateCustomer(next);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (items.length === 0) {
      setError(stripeCheckout ? 'Ihr Warenkorb enthält keine Artikel.' : 'Ihr Anfragekorb enthält keine Artikel.');
      return;
    }

    if (!formData.name.trim()) {
      setError('Bitte geben Sie Ihren vollständigen Namen an.');
      return;
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse an.');
      return;
    }

    if (!isValidPhone(formData.phone)) {
      setError('Bitte geben Sie eine gültige Telefonnummer an (z. B. +49 30 1234567).');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Bitte geben Sie eine Telefonnummer für Rückfragen an.');
      return;
    }

    if (!acceptTerms) {
      setError('Bitte bestätigen Sie die Datenschutzhinweise und Geschäftsbedingungen.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        tenantId: tenant?.id,
        customer: {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          street: formData.street.trim(),
          zip: formData.zip.trim(),
          city: formData.city.trim(),
        },
        notes: formData.notes.trim(),
        items: items.map(item => ({
          productId: item.productId || item.id,
          sku: item.sku || item.id,
          quantity: item.quantity,
          name: item.name,
          role: item.role || undefined,
          normCitation: item.normCitation || undefined,
        })),
      };

      if (stripeCheckout) {
        // a-047: echter Kauf. Der Server baut die Stripe-Session aus D1-Preisen; wir leiten nur weiter.
        const order = await submitOrder({
          tenantId: payload.tenantId,
          customer: payload.customer,
          notes: payload.notes,
          items: payload.items.map((i) => ({ productId: i.productId, sku: i.sku, quantity: i.quantity, name: i.name })),
        });
        if (order?.url) {
          window.location.href = order.url;
          return;
        }
        throw new Error(order?.error || 'Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
      }

      const res = await submitInquiry(payload);
      
      if (res && (res.ok || res.success || res.inquiry)) {
        const inquiryObj = res.inquiry || res;
        clearCart();
        if (onSuccess) {
          onSuccess(inquiryObj);
        } else if (onNavigate) {
          const inqId = inquiryObj.inquiryNumber || inquiryObj.inquiry_number || inquiryObj.id || '';
          onNavigate(`/inquiry/success/${inqId}`);
        }
      } else {
        throw new Error(res.error || res.message || 'Fehler beim Übermitteln der Anfrage.');
      }
    } catch (err) {
      setError(err.message || 'Die Anfrage konnte nicht übermittelt werden. Bitte prüfen Sie Ihre Angaben.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnerName = tenant?.name || 'Ihrem Fachpartner';

  return (
    <form onSubmit={handleSubmit} data-testid="quote-request-form" className="space-y-6">
      {error && (
        <Alert variant="destructive" data-testid="quote-error-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Übermittlung fehlgeschlagen</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Positionen-Übersicht (Pre-populated from Cart / BoM) */}
      <div className="space-y-3 rounded-2xl border border-[var(--border)] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-[var(--primary)]" />
            <h3 className="font-display text-base font-bold text-[var(--foreground)]">
              1. Angefragte Positionen
            </h3>
          </div>
          <span className="text-xs font-semibold text-[var(--secondary)]">
            {`${items.length} Position${items.length !== 1 ? 'en' : ''}`}
          </span>
        </div>

        {items.length === 0 ? (
          <div className="py-6 text-center text-xs text-[var(--secondary)]">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p>{stripeCheckout ? 'Ihr Warenkorb ist derzeit leer.' : 'Ihr Anfragekorb ist derzeit leer.'}</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {items.map((item, idx) => {
              const unitPrice = item.unitPriceCents || 0;
              const lineTotal = unitPrice * item.quantity;
              const role = item.role;
              const roleLabel = role ? (ROLE_LABELS[role] || role) : null;
              const roleColorClass = role ? (ROLE_COLORS[role] || 'bg-slate-50 text-slate-700 border-slate-200') : '';

              return (
                <div
                  key={`${item.sku || item.id}-${idx}`}
                  data-testid="quote-item-row"
                  className="flex items-center justify-between py-2.5 text-xs gap-3"
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <span className="font-bold text-[var(--foreground)] bg-slate-100 px-1.5 py-0.5 rounded text-xs shrink-0">
                      {item.quantity}&times;
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-semibold text-[var(--foreground)] truncate">{item.name}</p>
                        {role && (
                          <span className={`inline-flex items-center rounded px-1.5 py-0.2 text-xs font-semibold border ${roleColorClass}`}>
                            {roleLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[var(--secondary)] font-mono">
                        <span>Art.-Nr. {item.sku || item.productId || item.id}</span>
                        {item.manufacturer && <span>&bull; {item.manufacturer}</span>}
                        {item.normCitation && <span>&bull; {item.normCitation}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="font-bold text-[var(--foreground)]">
                      {unitPrice > 0 ? formatCurrency(lineTotal) : 'Auf Anfrage'}
                    </p>
                    {item.quantity > 1 && unitPrice > 0 && (
                      <p className="text-xs text-[var(--secondary)]">{formatCurrency(unitPrice)} / Stk.</p>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between pt-3 text-xs font-semibold">
              <span className="text-[var(--secondary)]">Geschätzte Gesamtsumme (netto):</span>
              <span className="font-bold text-sm text-[var(--foreground)]">
                {!priceUnknown && subtotalCents > 0 ? formatCurrency(subtotalCents) : 'Auf Anfrage'}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-2xl border border-[var(--border)] bg-white p-6 shadow-sm">
        <h3 className="font-display text-base font-bold text-[var(--foreground)] border-b border-[var(--border)] pb-3">
          2. Ihre Kontaktdaten
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="inq-company" className="text-xs font-semibold text-[var(--foreground)]">
              Firma / Organisation <span className="text-[var(--secondary)] font-normal">(optional)</span>
            </label>
            <Input
              id="inq-company" name="company"
              value={formData.company}
              onChange={(e) => handleChange('company', e.target.value)}
              placeholder="z.B. Elektro & Sicherheit GmbH"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="inq-name" className="text-xs font-semibold text-[var(--foreground)]">
              Ansprechpartner / Name <span className="text-red-500">*</span>
            </label>
            <Input
              required
              id="inq-name" name="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Vor- und Nachname"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="inq-email" className="text-xs font-semibold text-[var(--foreground)]">
              E-Mail-Adresse <span className="text-red-500">*</span>
            </label>
            <Input
              required
              type="email"
              id="inq-email" name="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="ihre.adresse@firma.de"
            />
          </div>

          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="inq-phone" className="text-xs font-semibold text-[var(--foreground)]">
              Telefonnummer für Rückfragen <span className="text-red-500">*</span>
            </label>
            <Input
              required
              type="tel"
              id="inq-phone" name="phone"
              pattern="\+?[0-9][0-9 \-/()]{4,}[0-9]"
              title="Telefonnummer, z. B. +49 30 1234567"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+49 ..."
            />
          </div>
        </div>
      </div>

      <div className="space-y-4 rounded-2xl border border-[var(--border)] bg-white p-6 shadow-sm">
        <h3 className="font-display text-base font-bold text-[var(--foreground)] border-b border-[var(--border)] pb-3">
          3. Projekt- & Lieferadresse <span className="text-[var(--secondary)] font-normal text-xs">(optional)</span>
        </h3>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1 sm:col-span-3">
            <label htmlFor="inq-street" className="text-xs font-semibold text-[var(--foreground)]">Straße & Hausnummer</label>
            <Input
              id="inq-street" name="street"
              value={formData.street}
              onChange={(e) => handleChange('street', e.target.value)}
              placeholder="Musterstraße 123"
            />
          </div>

          <div className="space-y-1 sm:col-span-1">
            <label htmlFor="inq-zip" className="text-xs font-semibold text-[var(--foreground)]">PLZ</label>
            <Input
              id="inq-zip" name="zip"
              value={formData.zip}
              onChange={(e) => handleChange('zip', e.target.value)}
              placeholder="12345"
            />
          </div>

          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="inq-city" className="text-xs font-semibold text-[var(--foreground)]">Ort</label>
            <Input
              id="inq-city" name="city"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Musterstadt"
            />
          </div>

          <div className="space-y-1 sm:col-span-3">
            <label htmlFor="inq-notes" className="text-xs font-semibold text-[var(--foreground)]">Anmerkungen oder Projekthinweise</label>
            <Textarea
              id="inq-notes" name="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Gewünschter Liefertermin, Projektdetails, technische Besonderheiten..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Terms & Submit */}
      <div className="space-y-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-container-low)] p-6">
        <label className="flex items-start gap-3 text-xs text-[var(--foreground)] cursor-pointer">
          <input
            type="checkbox"
            required
            name="acceptTerms"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--ring)] cursor-pointer"
          />
          <span className="leading-relaxed">
            Ich habe die{' '}
            <button
              type="button"
              onClick={() => onNavigate?.('/agb')}
              className="text-[var(--primary)] font-semibold hover:underline cursor-pointer"
            >
              AGB
            </button>{' '}
            und{' '}
            <button
              type="button"
              onClick={() => onNavigate?.('/datenschutz')}
              className="text-[var(--primary)] font-semibold hover:underline cursor-pointer"
            >
              Datenschutzerklärung
            </button>{' '}gelesen.
          </span>
        </label>

        <Button
          type="submit"
          size="lg"
          disabled={isSubmitting || items.length === 0}
          data-testid="submit-inquiry-btn"
          className="w-full gap-2 font-bold text-base shadow-sm cursor-pointer"
        >
          {isSubmitting ? (
            <span>{stripeCheckout ? 'Sie werden zur Zahlung weitergeleitet...' : 'Anfrage wird übermittelt...'}</span>
          ) : stripeCheckout ? (
            <>
              <ShieldCheck className="h-5 w-5" />
              Zur Kasse
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              Unverbindliche Bestellanfrage einreichen
            </>
          )}
        </Button>

        <div className="flex items-center justify-center gap-2 text-center text-xs text-[var(--secondary)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[var(--primary)]" />
          <span>{stripeCheckout ? 'Zahlung sicher über Stripe. Preise inkl. MwSt.' : 'Ihre Anfrage ist 100% unverbindlich und kostenfrei.'}</span>
        </div>
      </div>
    </form>
  );
}

